import { type Random } from '@jet/environment';
import {
    inject,
    ObjectGraph,
} from '@jet/environment/dependencies/object-graph';
import { makeMetatype } from '@jet/environment/util/metatype';
import { ObjectGraphType } from '../dependencies';
import { Logger } from '../dependencies/logger';

export class IDProvider {
    private readonly itemIDs: Set<string> = new Set();
    private readonly sectionIDs: Set<string> = new Set();
    private readonly random: Random;
    readonly objectGraph: ObjectGraph;

    constructor(objectGraph: ObjectGraph) {
        this.objectGraph = objectGraph;
        this.random = inject(ObjectGraphType.random, objectGraph);
    }

    // This produces a unique ID for each item or in a page in order to meet the requirement of the
    // ShelvesPresenter, that items have unique IDs across the page. We attempt to produce a
    // stable ID, so that we don't break the animations provided by NSDiffableDataSourcemusic-classical
    // upon refreshing the page (or part of it), but in certain situations this may not be
    // possible, e.g. when we have duplicate items in the same shelf.
    uniqueItemID(
        sectionID: string,
        itemID: string,
        isOnPageWithPagination?: boolean,
    ): string {
        let id = `${sectionID} - ${itemID}`;

        // When paginating we don't know what IDs have been used on previous paginations,
        // so just be safe and use a UUID.
        if (isOnPageWithPagination || this.itemIDs.has(id)) {
            if (!isOnPageWithPagination) {
                Logger.warn(this.objectGraph, `Duplicate itemID '${id}'.`);
            }

            id = `${id} - ${this.random.nextUUID()}`;
        }

        this.itemIDs.add(id);

        return id;
    }

    uniqueSectionID(sectionID: string): string {
        let id = sectionID;

        if (this.sectionIDs.has(id)) {
            Logger.warn(this.objectGraph, `Duplicate sectionID '${id}'.`);
            id = `${id} - ${this.random.nextUUID()}`;
        }

        this.sectionIDs.add(id);

        return id;
    }
}

// tslint:disable-next-line:variable-name
export const IDProviderType = makeMetatype<IDProvider>('idProvider');

export function uniqueItemID(
    sectionID: string,
    itemID: string,
    objectGraph: ObjectGraph,
    isOnPageWithPagination?: boolean,
): string {
    const idProvider = inject(IDProviderType, objectGraph);
    return idProvider.uniqueItemID(sectionID, itemID, isOnPageWithPagination);
}

export function uniqueSectionID(
    sectionID: string,
    objectGraph: ObjectGraph,
): string {
    const idProvider = inject(IDProviderType, objectGraph);
    return idProvider.uniqueSectionID(sectionID);
}
